import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';

import ConfirmationDialog from "./ConfirmationDialog";

QuickCreateDialogs.propTypes = {
    value: PropTypes.string,
    tabName: PropTypes.string,
    renderCreateDialog: PropTypes.func.isRequired,
    onCancelToList: PropTypes.func.isRequired,
    onEnd: PropTypes.func.isRequired,
};

/**
 * State machine of the quick-create flow, shared by the mapping rows: the
 * confirmation dialog first, then the type-specific creation dialog provided
 * through `renderCreateDialog({isOpen, initialName, close})`.
 *
 * `value` is the text typed in the mapping select and drives the flow:
 * non-null opens the confirmation, `onEnd` asks the host to clear it. On
 * cancel the host is asked to reopen its select with the typed value
 * restored (`onCancelToList`), as required by the ticket.
 */
export default function QuickCreateDialogs({value, tabName, renderCreateDialog, onCancelToList, onEnd}) {
    const [isFormOpen, setFormOpen] = useState(false);
    const [cancelledValue, setCancelledValue] = useState(null);

    const isFlowOpen = null !== value && undefined !== value;

    // a new flow always starts on the confirmation phase
    useEffect(() => {
        if (isFlowOpen) {
            setFormOpen(false);
        }
    }, [value]);

    function close() {
        setFormOpen(false);
        onEnd();
    }

    function handleCancel() {
        setCancelledValue(value);
        onEnd();
    }

    // reopening while the dialog is still closing would leave the freshly
    // focused select blurred (and closed) by the dialog teardown, so the
    // host select is only reopened once the dialog has fully exited
    function handleConfirmationExited() {
        if (null === cancelledValue) {
            return;
        }

        setCancelledValue(null);
        onCancelToList(cancelledValue);
    }

    return (
        <>
            <ConfirmationDialog
                isOpen={isFlowOpen && !isFormOpen}
                value={value}
                tabName={tabName}
                onConfirm={() => setFormOpen(true)}
                onCancel={() => handleCancel()}
                onExited={() => handleConfirmationExited()}
            />
            {renderCreateDialog({
                isOpen: isFlowOpen && isFormOpen,
                initialName: value,
                close,
            })}
        </>
    );
}
